// Used guide from here: https://docs.pmnd.rs/zustand/guides/nextjs
import { addDays, addMonths } from "date-fns"
import { createStore } from "zustand/vanilla"
import { persist, createJSONStorage } from "zustand/middleware"

import {
  convertToStartOfMonthUTC,
  convertToStartOfWeekUTC,
  startOfTodayUTC,
} from "~/utils/datetime"

export interface DateRange {
  start: Date
  end: Date
}

export type DateRangeState = {
  weeklyStartDate: Date
  weeklyEndDate: Date
  monthlyStartDate: Date
  monthlyEndDate: Date
  /**
   * The product the user has explicitly picked a date range for. While the
   * user is on this product, the variant-level auto-fit effect leaves the
   * range alone. Cleared (set to null) when navigating to a different
   * product so the new product still gets its initial auto-fit.
   */
  userOverrideProductId: string | null
}

export type DateRangeActions = {
  updateWeeklyDates: (start: Date, end: Date) => void
  updateMonthlyDates: (start: Date, end: Date) => void
  setUserOverrideProductId: (productId: string | null) => void
}

export type DateRangeStore = DateRangeState & DateRangeActions

export const initDateRangeStore = (): DateRangeState => {
  const today = startOfTodayUTC()
  return {
    weeklyStartDate: addDays(convertToStartOfWeekUTC(today), -364),
    weeklyEndDate: addDays(convertToStartOfWeekUTC(today), 364),
    monthlyStartDate: convertToStartOfMonthUTC(addMonths(today, -12)),
    monthlyEndDate: convertToStartOfMonthUTC(addMonths(today, 12)),
    userOverrideProductId: null,
  }
}

export const defaultInitState: DateRangeState = initDateRangeStore()

export const createDateRangeStore = (initState: DateRangeState = defaultInitState) => {
  return createStore<DateRangeStore>()(
    persist(
      (set) => ({
        ...initState,
        updateWeeklyDates: (start, end) =>
          set(() => ({
            weeklyStartDate: convertToStartOfWeekUTC(start),
            weeklyEndDate: convertToStartOfWeekUTC(end),
          })),
        updateMonthlyDates: (start, end) =>
          set(() => ({
            monthlyStartDate: convertToStartOfMonthUTC(start),
            monthlyEndDate: convertToStartOfMonthUTC(end),
          })),
        setUserOverrideProductId: (productId) => set(() => ({ userOverrideProductId: productId })),
      }),
      {
        name: "date-range-store-v2",
        storage: createJSONStorage(() => localStorage, {
          reviver: (key, value) => {
            if (
              key === "weeklyStartDate" ||
              key === "weeklyEndDate" ||
              key === "monthlyStartDate" ||
              key === "monthlyEndDate"
            ) {
              return new Date(value as string)
            }
            return value
          },
          replacer: (_, value) => {
            if (value instanceof Date) {
              return value.toISOString()
            }
            return value
          },
        }),
      },
    ),
  )
}
